/**
 * The film's colors, read off the site's tokens.
 *
 * A color is a token in `styles/tokens.css` and nowhere else (CLAUDE.md), and a canvas is
 * no exception: the film reads `--film-*` and the site's own accent off the document, so
 * a token changed in one place recolors the film with the rest of the site.
 */

export interface Palette {
  paper: string;
  grid: string;
  ink: string;
  sky: string;
  grass: string;
  wood: string;
  woodDark: string;
  siding: string;
  roof: string;
  trim: string;
  glass: string;
  shadow: string;
  /** The site's accent: the cap, the button, the door, the price. */
  accent: string;
  /** The flag on a load of lumber, the dog's collar. */
  signal: string;
  font: string;
}

const TOKENS: Record<keyof Palette, string> = {
  paper: "--film-paper",
  grid: "--film-grid",
  ink: "--film-ink",
  sky: "--film-sky",
  grass: "--film-grass",
  wood: "--film-wood",
  woodDark: "--film-wood-dark",
  siding: "--film-siding",
  roof: "--film-roof",
  trim: "--white",
  glass: "--film-glass",
  shadow: "--film-shadow",
  accent: "--accent",
  signal: "--error",
  font: "--font-sans",
};

/** The palette as the document's stylesheet has it. */
export function paletteFrom(style: Pick<CSSStyleDeclaration, "getPropertyValue">): Palette {
  const entries = Object.entries(TOKENS).map(([key, token]) => {
    const value = style.getPropertyValue(token).trim();
    if (value === "") {
      throw new Error(`the film needs ${token} from tokens.css`);
    }
    return [key, value];
  });
  return Object.fromEntries(entries) as unknown as Palette;
}

/** Every token the film reads, for the test that holds `tokens.css` to them. */
export const FILM_TOKENS: readonly string[] = Object.values(TOKENS);
